// LIBS
import React, {useEffect, useState} from "react";
import useInterval from "../hooks/useInterval";
import {Button} from "antd";
import {AimOutlined, CaretRightOutlined} from "@ant-design/icons";
import {useGoogleMap} from "@react-google-maps/api";
import Countdown, {zeroPad} from 'react-countdown';
import {useTranslation} from "react-i18next";
// INTERNAL
import TrackerService from "../services/tracker.service";
import race from "../services/race.service";

const GET_ACTIVATED_RACE_FREQUENCY = 1000;
const STANDBY_DELAY = 1000; // ms between 'all standby' and switching selected trackers on

export const HeatsMapToolbar = React.forwardRef((props, ref) => {
    const {t} = useTranslation();
    const {nextStartTime, selectedTrackers, onHeatsActiveChange, hasVenue} = props;

    const [engineOn, setEngineOn] = useState(false);     // HPv2 engine running (heats OR a normal race)
    const [raceActive, setRaceActive] = useState(false); // a normal race is activated

    // the HPv2 engine is also on during a normal race : heats are running only
    // when the engine is on and no race is active
    const heatsActive = engineOn && !raceActive;

    // let the page know whether the heats are running
    useEffect(() => {
        if (onHeatsActiveChange) onHeatsActiveChange(heatsActive);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [heatsActive]);

    // heats can be started (even with no horse selected) if a venue is selected, the engine
    // is off and no race is active ; they can't be started nor stopped while a race is active
    const startEnable = !!hasVenue && !engineOn && !raceActive;
    const stopEnable = heatsActive;

    // refresh the activated race and HPv2 engine state
    const refreshEngineState = () => {
        race.getActivatedRace().then(r => {
            setRaceActive(!!(r && r.data && r.data !== 'none'));
        });
        TrackerService.getHpv2EngineState().then(r => {
            setEngineOn(!!(r && r.data && r.data.state));
        });
    };

    // check immediately on mount so the buttons are in the right state right away
    useEffect(() => {
        refreshEngineState();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // then poll so the toolbar stays in sync
    useInterval(refreshEngineState, GET_ACTIVATED_RACE_FREQUENCY);

    // handlers -------------------------------------------------------
    const startHeats = () => {
        if (!startEnable) return;

        // 1. tous en standby
        TrackerService.setAllTrackersState({state: "standby"}).then();

        // 2. attendre 1s, allumer les trackers sélectionnés, démarrer le moteur HPv2 uniquement
        // (pas de racing-python : les heats n'ont pas de contexte de course)
        setTimeout(() => {
            // n'activer que les capteurs sélectionnés (all_state "live" allumerait TOUS les capteurs)
            (selectedTrackers || []).forEach(tr => {
                TrackerService.setTrackerState({id: tr, state: "live"}).then();
            });
            TrackerService.setHpv2EngineState(true).then();
            setEngineOn(true);
        }, STANDBY_DELAY);
    };

    const stopHeats = () => {
        if (!stopEnable) return;
        // arrêter le moteur HPv2 uniquement, puis repasser tous les capteurs en standby
        TrackerService.setHpv2EngineState(false).then();
        TrackerService.setAllTrackersState({state: "standby"}).then();
        setEngineOn(false);
    };

    // blinking status shown under the start button (same as the pilotage page)
    const statusLabel = raceActive ? t('monitoring.race_running_short')
                      : heatsActive ? t('monitoring.heats_running_short')
                      : null;

    // CountDownToRace ----------------------------------------------
    const CountDownToRace = () => {
        return (
            <Countdown
                date={nextStartTime}
                renderer={({hours, minutes, seconds, completed}) => {
                    if (completed) {
                        return t('monitoring.race_started');
                    } else {
                        return t('monitoring.next_start')+ ' ' + zeroPad(hours)+':'+zeroPad(minutes)+':'+zeroPad(seconds)
                    }
                }}
            />
        )
    }

    // render ---------------------------------------------------------
    return (
        <div className="map_pilotage_toolbar">
            <div className="map_pilotage_toolbar_header">
                {nextStartTime?
                <span className="next_start">
                  <CountDownToRace/>
                </span>
                :
                <span className="next_start_hidden">next_start</span>
                }
            </div>
            <div className="map_pilotage_toolbar_button_wrapper">
                <div className="tlb-btn-wrapper">
                    <Button
                        className={`tlb-btn tlb-custom ${(!startEnable)? 'disable':''}`}
                        onClick={startHeats}
                        shape="circle"
                        icon={<CaretRightOutlined />}>
                    </Button>
                    <span className={statusLabel ? 'heats_running_blink' : ''}>{statusLabel || t('heats.btn_start')}</span>
                </div>

                <div className="tlb-btn-wrapper">
                    <Button
                        className={`tlb-btn tlb-custom ${(!stopEnable)? 'disable':''}`}
                        onClick={stopHeats}
                        shape="circle"
                        icon={
                            <svg width="13" height="13" viewBox="0 0 13 13" aria-hidden="true">
                                <rect width="13" height="13" fill="currentColor"/>
                            </svg>
                        }>
                    </Button>
                    <span>{t('heats.btn_stop')}</span>
                </div>
            </div>
        </div>
    );
});

/* -------------------------------------------------------------------------------- *\
|   HeatsFitButton : bouton en bas au centre de la carte pour centrer sur les pastilles.
|   Doit être rendu à l'intérieur de la GoogleMap (children de MonitoringMap).
\* -------------------------------------------------------------------------------- */
export const HeatsFitButton = ({positions}) => {
    const {t} = useTranslation();
    const map = useGoogleMap();
    const hasPositions = !!(positions && positions.length);

    const fit = () => {
        if (!map || !hasPositions) return;
        if (positions.length === 1) {
            map.setCenter(positions[0]);
            map.setZoom(18);
            return;
        }
        const bounds = new window.google.maps.LatLngBounds();
        positions.forEach(p => bounds.extend(p));
        map.fitBounds(bounds, 60);
    };

    return (
        <Button
            className="heats_fit_btn"
            type="primary"
            disabled={!hasPositions}
            icon={<AimOutlined/>}
            onClick={fit}>
            {t('heats.btn_center')}
        </Button>
    );
};

export default HeatsMapToolbar;
